import { useSceneStore } from "@/lib/sceneStore";
import {
  BufferAttribute,
  BufferGeometry,
  Color,
  ColorRepresentation,
  Mesh,
  MeshBasicMaterial,
  Points,
  ShaderMaterial,
  SphereGeometry,
  Uniform,
} from "three";
import { getDirtTexture } from "./Seabed";
import { Bubbles } from "./BubblesPoints";

export const DEFAULT_SUSPENDED_PARTICLE_COUNT = 20_000;
export const MAX_SUSPENDED_PARTICLE_COUNT = 60_000;
export const MIN_POLLUTION_EFFECT_SCALE = 0.05;
export const MAX_POLLUTION_EFFECT_SCALE = 18;
export const DEFAULT_SUSPENDED_VERTICAL_DRIFT_SCALE = 0.02;
export const MAX_SUSPENDED_VERTICAL_DRIFT_SCALE = 3;
export const DEFAULT_SUSPENDED_MOTION_SPEED_SCALE = 0.04;
export const MAX_SUSPENDED_MOTION_SPEED_SCALE = 6;
export const DEFAULT_SUSPENDED_HORIZONTAL_DRIFT_SCALE = 0.03;
export const MAX_SUSPENDED_HORIZONTAL_DRIFT_SCALE = 6;
export const POLLUTION_LEVEL_TRANSITION_SECONDS = 2;

export type SuspendedPollutionEffectConfig = {
  kind: "suspended";
  intensity?: number;
  particleCount?: number;
  particleSizeScale?: number;
  particleOpacityScale?: number;
  motionSpeedScale?: number;
  horizontalDriftScale?: number;
  verticalDriftScale?: number;
  particleColor?: ColorRepresentation;
};

export type ChemicalPollutionEffectConfig = {
  kind: "chemical";
  intensity?: number;
  opacityScale?: number;
};

export type SoapPollutionEffectConfig = {
  kind: "soap";
  intensity?: number;
  particleCount?: number;
};

export type PollutionEffectConfig =
  | SuspendedPollutionEffectConfig
  | ChemicalPollutionEffectConfig
  | SoapPollutionEffectConfig;

type PollutionRuntimeEffect = {
  object: Mesh | Points;
  setPollutionLevel: (level: number) => void;
  update: (time: number) => void;
  dispose: () => void;
};

type SolidParticleSeed = {
  baseX: number;
  baseY: number;
  baseZ: number;
  size: number;
  speed: number;
  phase: number;
  brightness: number;
};

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));
const clamp01 = (value: number) => clamp(value, 0, 1);
const lerp = (from: number, to: number, progress: number) =>
  from + (to - from) * progress;
const POLLUTION_LEVEL_EPSILON = 0.0001;

const solidVertexShader = /* glsl */ `
  attribute float particleSize;
  attribute float particleBrightness;
  attribute float particleSpeed;
  attribute float particlePhase;

  uniform float time;
  uniform float level;
  uniform float sizeScale;
  uniform float opacity;
  uniform float motionSpeedScale;
  uniform float horizontalDriftScale;
  uniform float verticalDriftScale;

  varying float vBrightness;
  varying float vDepthFade;
  varying float vDistanceToCamera;
  varying vec2 vUv;
  varying vec3 vWorldPosition;

  void main() {
    float drift = mix(0.16, 0.54, level);
    float horizontalDrift = drift * horizontalDriftScale;
    float spread = mix(0.82, 1.12, level);
    float wave = time * particleSpeed * motionSpeedScale + particlePhase;

    vec3 animatedPosition = position;
    animatedPosition.x = position.x * spread + sin(wave * 1.7) * horizontalDrift;
    animatedPosition.y = position.y + sin(wave * 0.62) * drift * 0.18 * verticalDriftScale;
    animatedPosition.z = position.z * spread + cos(wave * 1.3) * horizontalDrift;

    vec4 mvPosition = modelViewMatrix * vec4(animatedPosition, 1.0);
    float perspectiveScale = 230.0 / max(-mvPosition.z, 0.001);
    gl_PointSize = particleSize * sizeScale * perspectiveScale;
    gl_Position = projectionMatrix * mvPosition;

    vBrightness = particleBrightness;
    vDepthFade = smoothstep(-7.0, 2.8, animatedPosition.y) * opacity;
    vDistanceToCamera = -mvPosition.z;
    vWorldPosition = animatedPosition.xyz;
    vUv = uv;
  }
`;

const solidFragmentShader = /* glsl */ `
  uniform vec3 particleColor;
  uniform sampler2D tilesSampler;
  varying vec2 vUv;
  varying float vBrightness;
  varying float vDepthFade;
  varying float vDistanceToCamera;
  varying vec3 vWorldPosition;

  const float TILES_PER_AXIS = 4.0;
  const float TILE_SIZE = 1.0 / TILES_PER_AXIS;

  void main() {
    vec2 c = gl_PointCoord - vec2(0.5);
    float dist = length(c);
    if (dist > 0.5) discard;

    vec2 atlasUv = (vUv + gl_PointCoord) * TILE_SIZE;
    float maskAlpha = texture2D(tilesSampler, atlasUv).a;

    float blurAmount = 1.0 - smoothstep(1.0, 3.0, vDistanceToCamera);
    float sunFactor = smoothstep(-1.0, 4.0, vWorldPosition.y);

    maskAlpha = smoothstep(sunFactor, max(sunFactor, blurAmount), maskAlpha);


    float alpha = vDepthFade * (0.42 + vBrightness * 0.58) * maskAlpha;


    float shapeFactor = smoothstep(0.7, 1.0, 1.0 - gl_PointCoord.y);
    float brightness = vBrightness * mix(0.5, 1.0, shapeFactor);
    vec3 color = particleColor * (0.32 + (brightness) * 0.74);

    color = mix(color, color * 2.0, sunFactor);

    // color = vec3();
    // alpha = 1.0;

    gl_FragColor = vec4(color, alpha);
  }
`;

export function normalizePollutionIntensity(intensity: number | undefined) {
  return typeof intensity === "number" && Number.isFinite(intensity) && intensity > 0
    ? intensity
    : 1;
}

export function normalizePollutionEffectScale(
  scale: number | undefined,
  fallback = 1
) {
  return typeof scale === "number" && Number.isFinite(scale) && scale > 0
    ? clamp(scale, MIN_POLLUTION_EFFECT_SCALE, MAX_POLLUTION_EFFECT_SCALE)
    : fallback;
}

function getPollutionStrengthScale(
  baseScale: number | undefined,
  intensity: number | undefined
) {
  return normalizePollutionEffectScale(
    normalizePollutionEffectScale(baseScale) *
      normalizePollutionIntensity(intensity)
  );
}

export function normalizeSuspendedParticleCount(
  particleCount: number | undefined
) {
  if (
    typeof particleCount !== "number" ||
    !Number.isFinite(particleCount)
  ) {
    return DEFAULT_SUSPENDED_PARTICLE_COUNT;
  }

  return Math.round(clamp(particleCount, 0, MAX_SUSPENDED_PARTICLE_COUNT));
}

export function normalizeSuspendedVerticalDriftScale(
  verticalDriftScale: number | undefined
) {
  return typeof verticalDriftScale === "number" &&
    Number.isFinite(verticalDriftScale) &&
    verticalDriftScale >= 0
    ? clamp(verticalDriftScale, 0, MAX_SUSPENDED_VERTICAL_DRIFT_SCALE)
    : DEFAULT_SUSPENDED_VERTICAL_DRIFT_SCALE;
}

export function normalizeSuspendedMotionSpeedScale(
  motionSpeedScale: number | undefined
) {
  return typeof motionSpeedScale === "number" &&
    Number.isFinite(motionSpeedScale) &&
    motionSpeedScale >= 0
    ? clamp(motionSpeedScale, 0, MAX_SUSPENDED_MOTION_SPEED_SCALE)
    : DEFAULT_SUSPENDED_MOTION_SPEED_SCALE;
}

export function normalizeSuspendedHorizontalDriftScale(
  horizontalDriftScale: number | undefined
) {
  return typeof horizontalDriftScale === "number" &&
    Number.isFinite(horizontalDriftScale) &&
    horizontalDriftScale >= 0
    ? clamp(horizontalDriftScale, 0, MAX_SUSPENDED_HORIZONTAL_DRIFT_SCALE)
    : DEFAULT_SUSPENDED_HORIZONTAL_DRIFT_SCALE;
}

export function clampPollutionCollectedCount(
  collectedGarbageCount: number,
  totalGarbageCount: number
) {
  if (!Number.isFinite(totalGarbageCount) || totalGarbageCount <= 0) {
    return 0;
  }

  const collected = Number.isFinite(collectedGarbageCount)
    ? collectedGarbageCount
    : 0;

  return Math.round(Math.min(totalGarbageCount, Math.max(0, collected)));
}

export function getPollutionLevel(baseLevel: number) {
  return clamp01(baseLevel);
}

export function getPollutionTransitionLevel(
  fromLevel: number,
  toLevel: number,
  elapsedSeconds: number,
  durationSeconds = POLLUTION_LEVEL_TRANSITION_SECONDS
) {
  if (!Number.isFinite(durationSeconds) || durationSeconds <= 0) {
    return clamp01(toLevel);
  }

  const progress = Number.isFinite(elapsedSeconds)
    ? elapsedSeconds / durationSeconds
    : 0;

  return lerp(clamp01(fromLevel), clamp01(toLevel), clamp01(progress));
}

export function getPollutionLevelFromCounts(
  collectedGarbageCount: number,
  totalGarbageCount: number
) {
  if (!Number.isFinite(totalGarbageCount) || totalGarbageCount <= 0) {
    return 0;
  }

  const collected = Number.isFinite(collectedGarbageCount)
    ? collectedGarbageCount
    : 0;
  const dirtyProgress = 1 - clamp01(collected / totalGarbageCount);

  return dirtyProgress;
}

export class SolidPollutionParticles extends Points {
  private readonly seeds: SolidParticleSeed[] = [];
  private readonly opacityUniform: Uniform<number>;
  private readonly sizeUniform: Uniform<number>;
  private readonly timeUniform: Uniform<number>;
  private readonly levelUniform: Uniform<number>;
  private readonly particleColorUniform: Uniform<Color>;
  private readonly motionSpeedUniform: Uniform<number>;
  private readonly horizontalDriftUniform: Uniform<number>;
  private readonly verticalDriftUniform: Uniform<number>;
  private activeParticleCount = DEFAULT_SUSPENDED_PARTICLE_COUNT;
  private particleOpacityScale = 1;
  private particleSizeScale = 1;
  private motionSpeedScale = DEFAULT_SUSPENDED_MOTION_SPEED_SCALE;
  private horizontalDriftScale = DEFAULT_SUSPENDED_HORIZONTAL_DRIFT_SCALE;
  private verticalDriftScale = DEFAULT_SUSPENDED_VERTICAL_DRIFT_SCALE;
  private level = 0;
  private settingsSignature = "";

  constructor(roomSize: number, maxCount = MAX_SUSPENDED_PARTICLE_COUNT) {
    const particlePoolCount = Math.max(
      1,
      normalizeSuspendedParticleCount(maxCount)
    );
    const positionArray = new Float32Array(particlePoolCount * 3);
    const uvArray = new Float32Array(particlePoolCount * 2);
    const sizeArray = new Float32Array(particlePoolCount);
    const brightnessArray = new Float32Array(particlePoolCount);
    const speedArray = new Float32Array(particlePoolCount);
    const phaseArray = new Float32Array(particlePoolCount);
    const geometry = new BufferGeometry();

    geometry.setAttribute("position", new BufferAttribute(positionArray, 3));
    geometry.setAttribute("uv", new BufferAttribute(uvArray, 2));
    geometry.setAttribute("particleSize", new BufferAttribute(sizeArray, 1));
    geometry.setAttribute(
      "particleBrightness",
      new BufferAttribute(brightnessArray, 1)
    );
    geometry.setAttribute("particleSpeed", new BufferAttribute(speedArray, 1));
    geometry.setAttribute("particlePhase", new BufferAttribute(phaseArray, 1));

    const opacityUniform = new Uniform(0);
    const sizeUniform = new Uniform(1);
    const timeUniform = new Uniform(0);
    const levelUniform = new Uniform(0);
    const motionSpeedUniform = new Uniform(DEFAULT_SUSPENDED_MOTION_SPEED_SCALE);
    const particleColorUniform = new Uniform(new Color("#c5b89b"));
    const horizontalDriftUniform = new Uniform(
      DEFAULT_SUSPENDED_HORIZONTAL_DRIFT_SCALE
    );
    const verticalDriftUniform = new Uniform(
      DEFAULT_SUSPENDED_VERTICAL_DRIFT_SCALE
    );
    const material = new ShaderMaterial({
      transparent: true,
      depthWrite: false,
      depthTest: true,
      uniforms: {
        particleColor: particleColorUniform,
        time: timeUniform,
        level: levelUniform,
        opacity: opacityUniform,
        sizeScale: sizeUniform,
        motionSpeedScale: motionSpeedUniform,
        horizontalDriftScale: horizontalDriftUniform,
        verticalDriftScale: verticalDriftUniform,
        tilesSampler: new Uniform(getDirtTexture()),
      },
      vertexShader: solidVertexShader,
      fragmentShader: solidFragmentShader,
    });

    super(geometry, material);

    this.layers.set(1);

    this.activeParticleCount = Math.min(
      DEFAULT_SUSPENDED_PARTICLE_COUNT,
      particlePoolCount
    );
    this.opacityUniform = opacityUniform;
    this.sizeUniform = sizeUniform;
    this.timeUniform = timeUniform;
    this.levelUniform = levelUniform;
    this.particleColorUniform = particleColorUniform;
    this.motionSpeedUniform = motionSpeedUniform;
    this.horizontalDriftUniform = horizontalDriftUniform;
    this.verticalDriftUniform = verticalDriftUniform;
    this.name = "solid-pollution-particles";
    this.frustumCulled = false;
    this.renderOrder = 18;

    for (let index = 0; index < particlePoolCount; index += 1) {
      const seed = {
        baseX: Math.random() * roomSize - roomSize / 2,
        baseY: Math.random() * 9 - 4.1,
        baseZ: Math.random() * roomSize - roomSize / 2,
        size: 0.09 + Math.random() * 0.32,
        speed: 0.08 + Math.random() * 0.28,
        phase: Math.random() * Math.PI * 2,
        brightness: 0.32 + Math.random() * 0.98,
      };

      this.seeds.push(seed);
      const i3 = index * 3;
      positionArray[i3] = seed.baseX;
      positionArray[i3 + 1] = seed.baseY;
      positionArray[i3 + 2] = seed.baseZ;
      const tileIndex = Math.floor(Math.random() * 16);
      uvArray[index * 2] = tileIndex % 4;
      uvArray[index * 2 + 1] = Math.floor(tileIndex / 4);
      sizeArray[index] = seed.size;
      brightnessArray[index] = seed.brightness;
      speedArray[index] = seed.speed;
      phaseArray[index] = seed.phase;
    }

    this.setPollutionLevel(0);
  }

  setSettings(config: SuspendedPollutionEffectConfig) {
    const settingsSignature = [
      config.particleCount,
      config.particleSizeScale,
      config.particleOpacityScale,
      config.intensity,
      config.motionSpeedScale,
      config.horizontalDriftScale,
      config.verticalDriftScale,
      config.particleColor,
    ].join("|");

    if (settingsSignature === this.settingsSignature) {
      return;
    }

    this.settingsSignature = settingsSignature;
    this.activeParticleCount = Math.min(
      this.seeds.length,
      normalizeSuspendedParticleCount(config.particleCount)
    );
    this.particleSizeScale = normalizePollutionEffectScale(
      config.particleSizeScale
    );
    this.particleOpacityScale = getPollutionStrengthScale(
      config.particleOpacityScale,
      config.intensity
    );
    this.motionSpeedScale = normalizeSuspendedMotionSpeedScale(
      config.motionSpeedScale
    );
    this.horizontalDriftScale = normalizeSuspendedHorizontalDriftScale(
      config.horizontalDriftScale
    );
    this.verticalDriftScale = normalizeSuspendedVerticalDriftScale(
      config.verticalDriftScale
    );
    this.particleColorUniform.value = new Color(config.particleColor);
    this.motionSpeedUniform.value = this.motionSpeedScale;
    this.horizontalDriftUniform.value = this.horizontalDriftScale;
    this.verticalDriftUniform.value = this.verticalDriftScale;
    this.setPollutionLevel(this.level);
  }

  setPollutionLevel(level: number) {
    this.level = clamp01(level);
    const visibleCount = Math.round(this.activeParticleCount * this.level);
    
    // useSceneStore.getState().setPolutionLevel(0);

    this.visible = this.level > 0.01 && visibleCount > 0;
    this.geometry.setDrawRange(0, this.visible ? visibleCount : 0);
    this.opacityUniform.value =
      lerp(0, 0.82, this.level) * this.particleOpacityScale;
    this.sizeUniform.value =
      lerp(0.55, 1.22, this.level) * this.particleSizeScale;
    this.levelUniform.value = this.level;

    useSceneStore.getState().setPolutionLevel(this.level);
  }

  update(time: number) {
    if (!this.visible) return;
    this.timeUniform.value = time;
  }

  dispose() {
    this.geometry.dispose();
    const material = this.material;
    if (Array.isArray(material)) {
      material.forEach((item) => item.dispose());
    } else {
      material.dispose();
    }
  }
}

export class ChemicalWaterEffect extends Mesh {
  private readonly levelUniform: Uniform<number>;
  private readonly opacityScaleUniform: Uniform<number>;
  private readonly timeUniform: Uniform<number>;

  constructor(roomSize: number, roomHeight: number) {
    const radius = Math.max(roomSize, roomHeight) * 1.8;
    const geometry = new SphereGeometry(radius, 64, 32);
    const levelUniform = new Uniform(0);
    const opacityScaleUniform = new Uniform(1);
    const timeUniform = new Uniform(0);
    const material = new MeshBasicMaterial({
      transparent: true,
      opacity: 0,
    });

    super(geometry, material);

    this.visible = false;

    this.levelUniform = levelUniform;
    this.opacityScaleUniform = opacityScaleUniform;
    this.timeUniform = timeUniform;
    this.name = "chemical-water-effect";
    this.frustumCulled = false;
    this.renderOrder = 1_000_100;
    this.setPollutionLevel(0);
  }

  setOpacityScale(opacityScale: number | undefined) {
    this.opacityScaleUniform.value = normalizePollutionEffectScale(opacityScale);
  }

  setPollutionLevel(level: number) {
    const nextLevel = clamp01(level);

    useSceneStore.getState().setPolutionLevel(nextLevel);

    this.levelUniform.value = nextLevel;
  }

  update(time: number) {
    if (!this.visible) return;
    this.timeUniform.value = time;
  }

  dispose() {
    this.geometry.dispose();
    const material = this.material;
    if (Array.isArray(material)) {
      material.forEach((item) => item.dispose());
    } else {
      material.dispose();
    }
  }
}

function createTransitionedPollutionRuntimeEffect(
  object: Mesh | Points,
  prepareSettings: () => void,
  resolveLevel: (level: number) => number,
  applyLevel: (level: number) => void,
  updateObject: (time: number) => void,
  disposeObject: () => void
): PollutionRuntimeEffect {
  let initialized = false;
  let currentLevel = 0;
  let targetLevel = 0;
  let transitionFromLevel = 0;
  let transitionStartedAt = Number.NaN;

  return {
    object,
    setPollutionLevel: (level: number) => {
      prepareSettings();

      const nextTargetLevel = clamp01(resolveLevel(level));
      if (!initialized) {
        initialized = true;
        currentLevel = nextTargetLevel;
        targetLevel = nextTargetLevel;
        transitionFromLevel = nextTargetLevel;
        applyLevel(currentLevel);
        return;
      }

      if (Math.abs(nextTargetLevel - targetLevel) <= POLLUTION_LEVEL_EPSILON) {
        return;
      }

      transitionFromLevel = currentLevel;
      targetLevel = nextTargetLevel;
      transitionStartedAt = Number.NaN;
    },
    update: (time: number) => {
      if (
        initialized &&
        Math.abs(currentLevel - targetLevel) > POLLUTION_LEVEL_EPSILON
      ) {
        if (!Number.isFinite(transitionStartedAt)) {
          transitionStartedAt = time;
        }

        const elapsed = time - transitionStartedAt;
        currentLevel = getPollutionTransitionLevel(
          transitionFromLevel,
          targetLevel,
          elapsed
        );

        if (elapsed >= POLLUTION_LEVEL_TRANSITION_SECONDS) {
          currentLevel = targetLevel;
        }

        applyLevel(currentLevel);
      }

      updateObject(time);
    },
    dispose: disposeObject,
  };
}

export function createPollutionEffect(
  config: PollutionEffectConfig | undefined,
  roomSize: number,
  roomHeight: number
): PollutionRuntimeEffect | null {
  if (!config) return null;

  if (config.kind === "soap") {
    const object = new Bubbles(roomSize, 100000);

    object.count = 10000;

    return createTransitionedPollutionRuntimeEffect(
      object,
      () => {
        
      },
      (level: number) => getPollutionLevel(level),
      (level: number) => {
        useSceneStore.getState().setPolutionLevel(level);
        object.count = 10000 * level;
      },
      (time: number) => object.update(time),
      () => object.dispose()
    );
  }

  if (config.kind === "chemical") {
    const object = new ChemicalWaterEffect(roomSize, roomHeight);

    return createTransitionedPollutionRuntimeEffect(
      object,
      () => {
        object.setOpacityScale(
          getPollutionStrengthScale(config.opacityScale, config.intensity)
        );
      },
      (level: number) => getPollutionLevel(level),
      (level: number) => object.setPollutionLevel(level),
      (time: number) => object.update(time),
      () => object.dispose()
    );
  }

  const object = new SolidPollutionParticles(roomSize);

  return createTransitionedPollutionRuntimeEffect(
    object,
    () => {
      object.setSettings(config);
    },
    (level: number) => getPollutionLevel(level),
    (level: number) => object.setPollutionLevel(level),
    (time: number) => object.update(time),
    () => object.dispose()
  );
}
